import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AppRoutePolicyProvider } from "@/components/layout/AppRoutePolicyContext";
import { resolveRoutePolicy } from "@/lib/route-policies";

const ROUTE_PENDING_SELECTOR = '[data-route-pending="true"], [data-route-loading]';

/**
 * Invisible sentinel rendered alongside the route content. Its effect only
 * runs once the route subtree has actually committed (lazy chunks resolved,
 * Suspense released). Route components that have committed placeholder UI can
 * mark that state with data-route-pending="true" so the policy timeout stays
 * armed until the route's data-dependent content is actually ready.
 */
function RouteContentCommittedSignal({ onCommitted }: { onCommitted: () => void }) {
  useEffect(() => {
    // Query state belongs to the page, so its loading marker can disappear without this
    // sibling sentinel rerendering. Observe that commit or a finished page times out later.
    const observer = new MutationObserver(checkReady);
    function checkReady() {
      if (document.querySelector(ROUTE_PENDING_SELECTOR)) return;
      observer.disconnect();
      onCommitted();
    }
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-route-pending', 'data-route-loading'],
    });
    checkReady();
    return () => observer.disconnect();
  }, [onCommitted]);
  return null;
}

/**
 * Applies the per-route policy (quick-add visibility, error recovery, and an
 * optional "route preparation" timeout) to the nested route tree.
 *
 * The timeout exists to surface a Retry affordance when a route never
 * finishes preparing. It must NEVER replace content that already rendered:
 * once the route subtree commits for the current pathname the timer is
 * disarmed and the timeout screen is suppressed. (Previously the timer kept
 * running after a successful render, so fully-rendered pages — e.g. the
 * public donor giving page — were replaced by the timeout screen after
 * `timeoutMs` of normal reading/form-filling time.)
 */
export function RoutePolicyBoundary({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const policy = useMemo(() => resolveRoutePolicy(pathname), [pathname]);
  const [timedOut, setTimedOut] = useState(false);
  const [committedPathname, setCommittedPathname] = useState<string | null>(null);

  // Content for the current pathname has rendered successfully — the route is
  // no longer "preparing", so the timeout no longer applies.
  const hasCommittedContent = committedPathname === pathname;

  const handleContentCommitted = useCallback(() => {
    setCommittedPathname(pathname);
  }, [pathname]);

  useEffect(() => {
    setTimedOut(false);
    if (!policy.timeoutMs || policy.timeoutMs <= 0) {
      return;
    }
    if (hasCommittedContent) {
      // Route content already rendered; never arm (or re-arm) the timer.
      return;
    }

    const timer = window.setTimeout(() => {
      setTimedOut(true);
    }, policy.timeoutMs);

    return () => {
      clearTimeout(timer);
      setTimedOut(false);
    };
  }, [policy.timeoutMs, pathname, hasCommittedContent]);

  if (timedOut && !hasCommittedContent && policy.errorRecovery !== "none") {
    return (
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 px-4 py-10 text-center">
        <p className="text-page-title font-semibold">This page is taking longer than expected.</p>
        <p className="text-meta text-muted-foreground">
          We timed out while preparing this route. You can retry or continue waiting.
        </p>
        <div className="flex justify-center gap-2">
          <Button variant="primary" onClick={() => window.location.reload()}>
            Retry
          </Button>
          <Button variant="ghost" onClick={() => setTimedOut(false)}>
            Continue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <AppRoutePolicyProvider policy={policy}>
      {children}
      <RouteContentCommittedSignal onCommitted={handleContentCommitted} />
    </AppRoutePolicyProvider>
  );
}
